import { toPaginate } from '@/utils/infrastructure';
import _ from 'lodash';
import env from '@/config/env';
import { VALIDATION_INTERNAL_ERRORS } from '@/utils/infrastructure/constants';
import CoreQueries from '@/utils/input/queries';
import {
  createAIAgent,
  deleteAIAgent,
  deleteRPAAgent,
  fetchAIAgents,
  fetchRPAAgents,
  getAIAgentById,
  getRPAAgentById,
  patchRPAAgentName,
  restoreAIAgent,
  toggleAIAgentActive,
  toggleRPAAgentActive,
  updateAIAgent,
  publishAIAgent,
} from './service';
import { aiFiltersConfig, rpaFiltersConfig } from './utils';

const AGENT_TYPES = Object.freeze({
  AI: 'ai',
  RPA: 'rpa',
  SKILLS: 'skills',
});

const meta = {
  page: 1,
  limit: env.DEFAULT_PAGE_SIZES[0],
  count: 0,
  totalPages: 0,
};

function createFetchEffect({ cachePrefix, cacheSelector, setType, service }) {
  return function* ({ payload }, { call, put, select }) {
    try {
      const state = yield select((state) => state.agents);
      const { limit: currentLimit } = state.pagination;
      const limit = payload?.limit || currentLimit;

      const cacheKey = `${cachePrefix}-${payload?.page || 1}-${limit}-${payload?.search || ''}-${
        payload?.filter || ''
      }`;

      const cache = cacheSelector(state);

      if (cache && cache.cacheKey === cacheKey) {
        yield put({
          type: setType,
          payload: {
            ...cache.response,
            page: payload?.page || 1,
            limit,
            cacheKey,
          },
        });
        return;
      }

      const response = yield call(service, payload);
      yield put({
        type: setType,
        payload: {
          ...response,
          page: payload?.page || 1,
          limit,
          cacheKey,
        },
      });
    } catch (error) {}
  };
}

function createAISaveEffect(serviceCall) {
  return function* ({ payload, params }, { call, put }) {
    try {
      const transformedPayload = { ...payload };
      if (transformedPayload.icon && typeof transformedPayload.icon !== 'object') {
        transformedPayload.iconUrl = transformedPayload.icon;
        delete transformedPayload.icon;
      }

      const res = yield call(serviceCall, transformedPayload);

      yield put({
        type: 'global/showNotificationsGlobal',
        payload: { visible: true, type: 'success' },
      });

      yield put({ type: 'saveAgent', payload: res });
      yield put({ type: 'clearAICache' });
      yield put({
        type: 'fetchAIAgents',
        payload: { ...params, readPriority: true },
      });
    } catch (error) {
      const validationErrors = error?.validationErrors || [];
      const itemDeletedError = validationErrors.find(
        (ve) =>
          ve.errorCode === VALIDATION_INTERNAL_ERRORS.VALIDATION_ITEM_DELETED_EXISTS &&
          ve.metadata?.id,
      );
      if (itemDeletedError) {
        const message =
          (itemDeletedError.errorMessages && itemDeletedError.errorMessages[0]) || error?.message;
        const agentId = itemDeletedError.metadata.id;
        yield put({
          type: 'showRestoreAgentModal',
          payload: { message, agentId, params },
        });
        return;
      }
      if (error.message) {
        yield put({
          type: 'global/showNotificationsForm',
          payload: {
            visible: true,
            type: 'error',
            message: error.message,
          },
          validationErrors: error.validationErrors,
        });
      }
    }
  };
}

function createRPASaveEffect(serviceCall) {
  return function* ({ payload, params }, { call, put }) {
    try {
      const res = yield call(serviceCall, payload.id, { name: payload.name });

      yield put({
        type: 'global/showNotificationsGlobal',
        payload: { visible: true, type: 'success' },
      });

      yield put({ type: 'saveAgent', payload: res });
      yield put({ type: 'clearRPACache' });
      yield put({
        type: 'fetchRPAAgents',
        payload: { ...params, readPriority: true },
      });
    } catch (error) {
      if (error.message) {
        yield put({
          type: 'global/showNotificationsForm',
          payload: {
            visible: true,
            type: 'error',
            message: error.message,
          },
          validationErrors: error.validationErrors,
        });
      }
    }
  };
}

const Model = {
  namespace: 'agents',
  state: {
    data: [],
    pagination: meta,
    searchCriteria: '',
    currentTab: 'ai',
    aiData: [],
    rpaData: [],
    aiPagination: meta,
    rpaPagination: meta,
    aiCache: null,
    rpaCache: null,
    lists: {
      ai: {
        data: [],
        pagination: meta,
        cache: null,
      },
      rpa: {
        data: [],
        pagination: meta,
        cache: null,
      },
    },
    filtersConfig: aiFiltersConfig,
    newAgent: false,
    selectedItem: null,
    view: false,
    id: null,
    agentType: 'ai',
    publishLoading: false,
    restoreAgentModal: {
      visible: false,
      message: '',
      agentIds: null,
      params: null,
    },
    // Launchpad Detalles / Editar — general settings (not AgentWizard)
    showForm: false,
    formDetail: false,
    formId: null,
    formItem: null,
    formLoading: false,
    formSuccess: false,
  },

  effects: {
    fetchAIAgents: createFetchEffect({
      cachePrefix: 'ai',
      cacheSelector: (state) => state.aiCache,
      setType: 'setAIAgents',
      service: fetchAIAgents,
    }),

    fetchRPAAgents: createFetchEffect({
      cachePrefix: 'rpa',
      cacheSelector: (state) => state.rpaCache,
      setType: 'setRPAAgents',
      service: fetchRPAAgents,
    }),

    *search({ payload }, { put, select }) {
      try {
        const state = yield select((state) => state.agents);
        const { currentTab } = state;

        yield put({
          type: 'setSearchCriteria',
          payload: payload.criteria,
        });

        if (currentTab === AGENT_TYPES.AI) {
          yield put({
            type: 'clearAICache',
          });
        } else {
          yield put({
            type: 'clearRPACache',
          });
        }
      } catch (error) {}
    },

    *setCurrentTab({ payload }, { put, select }) {
      try {
        const state = yield select((state) => state.agents);
        const { currentTab } = payload;

        yield put({
          type: 'updateCurrentTab',
          payload: {
            currentTab,
            filtersConfig:
              currentTab === AGENT_TYPES.AI
                ? aiFiltersConfig
                : currentTab === AGENT_TYPES.RPA
                ? rpaFiltersConfig
                : state.filtersConfig,
            agentType: currentTab,
          },
        });

        if (currentTab === AGENT_TYPES.SKILLS) {
          return;
        }

        if (currentTab === AGENT_TYPES.AI) {
          if (!state.aiCache || state.aiData.length === 0) {
            yield put({
              type: 'fetchAIAgents',
              payload: { page: 1 },
            });
          } else {
            yield put({
              type: 'setAIAgents',
              payload: {
                ...state.aiCache.response,
                cacheKey: state.aiCache.cacheKey,
              },
            });
          }
        } else if (currentTab === AGENT_TYPES.RPA) {
          if (!state.rpaCache || state.rpaData.length === 0) {
            yield put({
              type: 'fetchRPAAgents',
              payload: { page: 1 },
            });
          } else {
            yield put({
              type: 'setRPAAgents',
              payload: {
                ...state.rpaCache.response,
                cacheKey: state.rpaCache.cacheKey,
              },
            });
          }
        }
      } catch (error) {}
    },

    *cleanup(_, { put }) {
      yield put({ type: 'cleanupState' });
    },

    *addNewAgent({ payload }, { put }) {
      yield put({ type: 'addAgent', payload });
      if (!_.isNil(payload.id)) {
        yield put({ type: 'editOrViewAgent', payload });
      }
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
      if (payload?.state && payload?.id) {
        yield put({ type: 'fetchSettingsItem', payload: { id: payload.id } });
      }
    },

    *fetchSettingsItem({ payload }, { call, put, select }) {
      try {
        yield put({ type: 'queryFormLoading', payload: true });
        const formDetail = yield select((s) => !!s.agents?.formDetail);
        const query = formDetail ? CoreQueries.ManageAgents.detail : CoreQueries.ManageAgents.edit;
        const response = yield call(getAIAgentById, payload.id, query);
        const item = response?.data || response;
        yield put({ type: 'queryFormItem', payload: item });
      } catch (e) {
        yield put({ type: 'queryFormLoading', payload: false });
      }
    },

    *updateSettingsItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'queryFormLoading', payload: true });
        // Same icon normalisation as createAISaveEffect / agentBuilder
        // saveAgentChanges: an existing photo arrives as a URL string on the
        // form field `icon`, but PUT /ai/agent expects either a Dictionary
        // upload payload on `icon` or a plain URL on `iconUrl`.
        const body = { ...payload, id: payload.id };
        if (body.icon && typeof body.icon !== 'object') {
          body.iconUrl = body.icon;
          delete body.icon;
        }
        yield call(updateAIAgent, body);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'queryFormSuccess' });
        yield put({
          type: 'queryShowForm',
          payload: { state: false, id: null, item: null, detail: false },
        });
        yield put({ type: 'clearAICache' });
      } catch (e) {
        yield put({ type: 'queryFormLoading', payload: false });
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
      }
    },

    *closeNewAgent({ payload }, { put }) {
      yield put({ type: 'closeAgent', payload });
    },

    *editItemAgent({ payload }, { call, put, select }) {
      try {
        const state = yield select((state) => state.agents);
        const { agentType } = state;

        const response =
          agentType === 'ai'
            ? yield call(getAIAgentById, payload.id, payload.params)
            : yield call(getRPAAgentById, payload.id, payload.params);

        const table = response.data || response;

        const view = typeof payload.view !== 'undefined' ? payload.view : !!payload.detail;

        yield put({
          type: 'editAgent',
          payload: { ...payload, view, item: table },
        });
      } catch (e) {
        yield put({ type: 'global/loadingItem', payload: false });
      }
    },

    saveNewAgent: createAISaveEffect(createAIAgent),

    saveEditAgent: createAISaveEffect(updateAIAgent),

    saveEditRPAAgent: createRPASaveEffect(patchRPAAgentName),

    *deleteItem({ id, params, view }, { call, put, select }) {
      try {
        const state = yield select((state) => state.agents);
        const { agentType } = state;

        yield put({ type: `global/modalConfirm`, state: false, id: null });

        const res =
          agentType === 'ai' ? yield call(deleteAIAgent, id) : yield call(deleteRPAAgent, id);

        yield put({ type: 'saveAgent', payload: res });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        yield put({ type: agentType === 'ai' ? 'clearAICache' : 'clearRPACache' });

        yield put({
          type: agentType === 'ai' ? 'fetchAIAgents' : 'fetchRPAAgents',
          payload: { ...params, readPriority: true },
        });
      } catch (error) {
        yield put({ type: `global/modalConfirm`, state: false, id: null });

        if (error.message) {
          yield put({
            type: `global/showNotifications${view ? 'Form' : 'Global'}`,
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
              autoHide: view,
            },
          });
        }
      }
    },

    *editActive({ payload }, { call, put, select }) {
      try {
        const state = yield select((state) => state.agents);
        const { agentType } = state;

        agentType === 'ai'
          ? yield call(toggleAIAgentActive, payload.id, payload.isActive)
          : yield call(toggleRPAAgentActive, payload.id, payload.isActive);

        yield put({ type: 'editActives', payload });
      } catch (e) {}
    },

    *restoreAgent({ payload }, { call, put, select }) {
      try {
        const { agentId } = payload;
        const ids = Array.isArray(agentId) ? agentId : [agentId];
        const state = yield select((s) => s.agents);
        const params = state.restoreAgentModal?.params || { page: 1 };

        yield call(restoreAIAgent, ids);
        yield put({ type: 'closeRestoreAgentModal' });
        yield put({
          type: 'addAgent',
          payload: { state: false, item: null },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'clearAICache' });
        yield put({
          type: 'fetchAIAgents',
          payload: { ...params, readPriority: true },
        });
      } catch (err) {
        if (err?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: err.message },
          });
        }
      }
    },

    *publishItem({ payload }, { call, put }) {
      try {
        yield put({
          type: 'setPublishLoading',
          payload: true,
        });

        yield call(publishAIAgent, payload.id);

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({
          type: 'setPublishLoading',
          payload: false,
        });
      }
    },
  },

  reducers: {
    setAIAgents(state, { payload }) {
      const isCurrentTab = state.currentTab === 'ai';
      const nextAiPagination = {
        ...state.aiPagination,
        ...toPaginate({ ...state.aiPagination, ...payload }),
      };
      const nextPagination = isCurrentTab
        ? { ...state.pagination, ...toPaginate({ ...state.pagination, ...payload }) }
        : state.pagination;
      return {
        ...state,
        data: isCurrentTab ? payload.items || [] : state.data,
        aiData: payload.items || [],
        pagination: nextPagination,
        aiPagination: nextAiPagination,
        aiCache: {
          response: payload,
          cacheKey: payload.cacheKey,
        },
        lists: {
          ...state.lists,
          ai: {
            data: payload.items || [],
            pagination: nextAiPagination,
            cache: { response: payload, cacheKey: payload.cacheKey },
          },
        },
      };
    },

    setRPAAgents(state, { payload }) {
      const isCurrentTab = state.currentTab === 'rpa';
      const nextRpaPagination = {
        ...state.rpaPagination,
        ...toPaginate({ ...state.rpaPagination, ...payload }),
      };
      const nextPagination = isCurrentTab
        ? { ...state.pagination, ...toPaginate({ ...state.pagination, ...payload }) }
        : state.pagination;
      return {
        ...state,
        data: isCurrentTab ? payload.items || [] : state.data,
        rpaData: payload.items || [],
        pagination: nextPagination,
        rpaPagination: nextRpaPagination,
        rpaCache: {
          response: payload,
          cacheKey: payload.cacheKey,
        },
        lists: {
          ...state.lists,
          rpa: {
            data: payload.items || [],
            pagination: nextRpaPagination,
            cache: { response: payload, cacheKey: payload.cacheKey },
          },
        },
      };
    },

    setSearchCriteria(state, { payload }) {
      return {
        ...state,
        searchCriteria: payload,
        aiCache: null,
        rpaCache: null,
      };
    },

    /**
     * Pin `agentType` without side effects.
     *
     * Used by the launchpad's RPA tile commands so the shared
     * `editActive` / `deleteItem` sagas pick the right endpoint
     * without us having to fork the saga or carry implicit state
     * through `setCurrentTab` (which also triggers a refetch).
     */
    setAgentType(state, { payload }) {
      if (!payload?.agentType) return state;
      return { ...state, agentType: payload.agentType };
    },

    updateCurrentTab(state, { payload }) {
      const { currentTab } = payload;
      return {
        ...state,
        currentTab,
        filtersConfig: payload.filtersConfig,
        agentType: payload.agentType || currentTab,
        searchCriteria: '',
        data: currentTab === 'ai' ? state.aiData : state.rpaData,
        pagination: currentTab === 'ai' ? state.aiPagination : state.rpaPagination,
      };
    },

    cleanupState(state) {
      return {
        ...state,
        data: [],
        pagination: meta,
        searchCriteria: '',
        currentTab: 'ai',
        aiData: [],
        rpaData: [],
        aiPagination: meta,
        rpaPagination: meta,
        aiCache: null,
        rpaCache: null,
        lists: {
          ai: { data: [], pagination: meta, cache: null },
          rpa: { data: [], pagination: meta, cache: null },
        },
        filtersConfig: aiFiltersConfig,
        newAgent: false,
        selectedItem: null,
        view: false,
        id: null,
        agentType: 'ai',
        publishLoading: false,
        restoreAgentModal: {
          visible: false,
          message: '',
          agentIds: null,
          params: null,
        },
        showForm: false,
        formDetail: false,
        formId: null,
        formItem: null,
        formLoading: false,
        formSuccess: false,
      };
    },

    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: !!payload?.state,
        formDetail: !!payload?.detail,
        formId: payload?.state ? payload?.id ?? null : null,
        formItem: payload?.state ? payload?.item ?? state.formItem : null,
        formSuccess: false,
        ...(payload?.state ? {} : { formLoading: false }),
      };
    },

    queryFormLoading(state, { payload }) {
      return { ...state, formLoading: !!payload };
    },

    queryFormItem(state, { payload }) {
      return {
        ...state,
        formItem: payload,
        formLoading: false,
      };
    },

    queryFormSuccess(state) {
      return { ...state, formSuccess: true, formLoading: false };
    },

    clearFormSuccess(state) {
      return { ...state, formSuccess: false };
    },

    clearAICache(state) {
      return {
        ...state,
        aiCache: null,
      };
    },

    clearRPACache(state) {
      return {
        ...state,
        rpaCache: null,
      };
    },

    addAgent(state, { payload }) {
      return {
        ...state,
        newAgent: payload.state,
        selectedItem: payload.item,
        view: payload.view,
        id: payload.id,
        agentType: payload.agentType || state.agentType,
      };
    },

    editOrViewAgent(state) {
      return {
        ...state,
        selectedItem: {},
      };
    },

    editAgent(state, { payload }) {
      return {
        ...state,
        selectedItem: payload.item,
        view: payload.view,
        id: payload.id,
      };
    },

    closeAgent(state, { payload }) {
      return {
        ...state,
        newAgent: payload.state,
        selectedItem: state.selectedItem,
        view: state.view,
        id: state.id,
      };
    },

    saveAgent(state, { payload }) {
      if (!payload) {
        return {
          ...state,
          newAgent: false,
          selectedItem: null,
          view: false,
          id: null,
        };
      }

      const updatedData =
        state.currentTab === state.agentType ? [payload, ...state.data] : state.data;

      const updatedAiData = state.agentType === 'ai' ? [payload, ...state.aiData] : state.aiData;
      const updatedRpaData =
        state.agentType === 'rpa' ? [payload, ...state.rpaData] : state.rpaData;

      return {
        ...state,
        newAgent: false,
        selectedItem: null,
        view: false,
        id: null,
        data: updatedData,
        aiData: updatedAiData,
        rpaData: updatedRpaData,
      };
    },

    editActives(state, { payload }) {
      const updateItemInArray = (array, id, updates) =>
        array.map((item) => (item.id === id ? { ...item, ...updates } : item));

      return {
        ...state,
        data: updateItemInArray(state.data, payload.id, { isActive: payload.isActive }),
        aiData: updateItemInArray(state.aiData, payload.id, { isActive: payload.isActive }),
        rpaData: updateItemInArray(state.rpaData, payload.id, { isActive: payload.isActive }),
      };
    },

    setPublishLoading(state, { payload }) {
      return {
        ...state,
        publishLoading: payload,
      };
    },

    showRestoreAgentModal(state, { payload }) {
      const agentIds = payload?.agentIds ?? (payload?.agentId != null ? [payload.agentId] : null);
      return {
        ...state,
        restoreAgentModal: {
          visible: true,
          message: payload?.message || '',
          agentIds,
          params: payload?.params ?? null,
        },
      };
    },

    closeRestoreAgentModal(state) {
      return {
        ...state,
        restoreAgentModal: {
          visible: false,
          message: '',
          agentIds: null,
          params: null,
        },
      };
    },
  },
};

export default Model;
